import { handleCommonApiErrors } from "@/api/CommonApiErrorHandler";
import DeliveryTypesApi from "@/api/DeliveryTypesApi";
import PartnerInfoApi from "@/api/PartnerInfoApi";
import store from "@/store/store";
import {
  BlockableActivity,
  IDeliveryType,
  IPartnerInfo,
  Logger,
  IRootState,
  User,
  PartnerTier,
} from "@iloq/shared";
import { ActionTree, MutationTree } from "vuex";

const partnerInfoApi = new PartnerInfoApi();
const deliveryTypesApi = new DeliveryTypesApi();
export interface IPartnerInfoState {
  partnerInfo: any;
  deliveryTypes: IDeliveryType[];
}

export interface ActivitiesToCheck {
  anyOf?: BlockableActivity[];
  allOf?: BlockableActivity[];
}

const initialState: IPartnerInfoState = {
  partnerInfo: { marketingTier: PartnerTier.NONE, benefitTier: PartnerTier.NONE } as any,
  deliveryTypes: [],
};

const actions: ActionTree<IPartnerInfoState, IRootState> = {
  getPartnerInfo({ commit, dispatch }, user: User): Promise<any> {
    Logger.debug("action, getPartnerInfo");
    return partnerInfoApi
      .getPartnerInfo(user.email)
      .then((res: IPartnerInfo) => {
        commit("setPartnerInfoResult", res);
        commit("setPartnerIdToUser", res.partnerId, { root: true });
        dispatch(
          "productSearch/initializeState",
          {
            salesRegion: res.priceListAreaCode,
          },
          { root: true },
        );
        dispatch("orderingPermission/setOrderingPermissions", res.productGroups, { root: true });
        // Service products need to be in place before shopping cart initialization
        dispatch("productData/getServiceproducts", null, { root: true }).then(() =>
          dispatch("shoppingCart/initializeShoppingCart", null, { root: true }),
        );
        dispatch("productData/getServiceproducts", null, { root: true });
        dispatch("productData/getHandlingFee", null, { root: true });
        dispatch("productData/getSurfaceTreatmentFee", null, { root: true });
        dispatch(
          "events/fetchEvents",
          { countryCode: res.country, languageCode: user.languageCode },
          { root: true },
        );
      })
      .catch(handleCommonApiErrors);
  },
  fetchDeliveryTypes({ commit }): void {
    deliveryTypesApi
      .getDeliveryTypes()
      .then((data: IDeliveryType[]) => {
        commit("setDeliveryTypes", data);
      })
      .catch(handleCommonApiErrors);
  },
  handleBlockedActivity({ state }, activitiesToCheck: ActivitiesToCheck): boolean {
    const anyOfMatches: BlockableActivity[] = [];
    const allOfMatches: BlockableActivity[] = [];
    (state.partnerInfo.blockedActivities || []).forEach((blockedActivity: BlockableActivity) => {
      if ((activitiesToCheck.anyOf || []).includes(blockedActivity)) {
        anyOfMatches.push(blockedActivity);
      }
      if ((activitiesToCheck.allOf || []).includes(blockedActivity)) {
        allOfMatches.push(blockedActivity);
      }
    });

    const blocked =
      anyOfMatches.length > 0 ||
      (allOfMatches.length > 0 && allOfMatches.length === activitiesToCheck.allOf?.length);

    if (blocked) {
      store.commit("showBlockedActivityError");
    }
    return blocked;
  },
};

const mutations: MutationTree<IPartnerInfoState> = {
  setPartnerInfoResult(state: IPartnerInfoState, result: any): void {
    // Remove possible country code prefix from zip codes (iLOQ sometimes adds that to zip code in their ERP)
    result.zipCode = result.zipCode?.replace(/^[A-Za-z][A-Za-z]-/, "");
    state.partnerInfo = result;
  },
  setDeliveryTypes(state: IPartnerInfoState, deliveryTypes: IDeliveryType[]): void {
    state.deliveryTypes = deliveryTypes;
  },
};

export const partnerInfoModule = {
  actions,
  mutations,
  state: initialState,
  namespaced: true,
};
